'use client';

import Script from 'next/script';
import { useEffect, useId, useRef } from 'react';

// Cloudflare Turnstile в форме входа/регистрации. Если site-key не задан —
// компонент рендерит null и не подключает CF-скрипт (для локалки/CI).

type TurnstileWindow = Window & {
  turnstile?: {
    render: (
      target: HTMLElement | string,
      options: {
        sitekey: string;
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'compact';
        callback?: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
      },
    ) => string;
    remove: (widgetId: string) => void;
    reset: (widgetId?: string) => void;
  };
};

export function TurnstileWidget({ siteKey }: { siteKey: string | null }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const fieldName = 'cf-turnstile-response';
  const inputId = useId();

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;

    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      const ts = (window as TurnstileWindow).turnstile;
      if (!ts) {
        // CF-скрипт ещё не загрузился — повторим через 100 ms (загрузка ≈ 500 ms).
        setTimeout(tryRender, 100);
        return;
      }
      widgetIdRef.current = ts.render(containerRef.current!, {
        sitekey: siteKey,
        theme: 'auto',
        size: 'normal',
        callback: (token) => {
          const input = document.getElementById(inputId) as HTMLInputElement | null;
          if (input) input.value = token;
        },
        'expired-callback': () => {
          const input = document.getElementById(inputId) as HTMLInputElement | null;
          if (input) input.value = '';
        },
        'error-callback': () => {
          const input = document.getElementById(inputId) as HTMLInputElement | null;
          if (input) input.value = '';
        },
      });
    };
    tryRender();

    return () => {
      cancelled = true;
      const ts = (window as TurnstileWindow).turnstile;
      if (ts && widgetIdRef.current) {
        try {
          ts.remove(widgetIdRef.current);
        } catch {
          // Виджет мог уже размонтироваться сам — игнорируем.
        }
      }
    };
  }, [siteKey, inputId]);

  if (!siteKey) return null;

  return (
    <div className="space-y-2">
      <Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" strategy="lazyOnload" />
      <div ref={containerRef} />
      <input id={inputId} name={fieldName} type="hidden" />
    </div>
  );
}
